/*!
 * Copyright 2022 Thorne Health Lab. All rights reserved.
 */
import React, { useEffect, useState } from 'react';
import {
  find,
  forEach,
  get,
  has,
  isArray,
  lowerCase,
  map,
  filter,
  remove
} from 'lodash';

import DoubleText from './QuestionComponents/DoubleText';
import SingleText from './QuestionComponents/SingleText';
import Slider from './QuestionComponents/Slider';
import Radio from './QuestionComponents/Radio';
import Checkbox from './QuestionComponents/Checkbox';
import TextDropdown from './QuestionComponents/TextDropdown';
import EditSelect from './QuestionComponents/EditSelect';
import DoubleTextDropdown from './QuestionComponents/DoubleTextDropdown';
import Dropdown from './QuestionComponents/Dropdown';
import TextRadio from './QuestionComponents/TextRadio';
import RadioGrid from './QuestionComponents/RadioGrid';
import DropdownRadio from './QuestionComponents/DropdownRadio';
import CheckboxGrid from './QuestionComponents/CheckboxGrid';
import Message from './QuestionComponents/Message';
import TimePicker from './QuestionComponents/TimePicker';
import TimeEditor from './QuestionComponents/TimeEditor';
import Element from '../../../components/element';
import Button from '../../../components/Button';
import Flex from '../../../components/flex';
import FilterList from './QuestionComponents/FilterList/index';
import { isNullOrUndefined } from '../../../utils';
import medications from '../../../jsons/medicationList.json';

const components = {
  SingleText,
  DoubleText,
  Slider,
  Radio,
  Checkbox,
  Dropdown,
  TextDropdown,
  EditSelect,
  DropdownRadio,
  TextRadio,
  RadioGrid,
  CheckboxGrid,
  DoubleTextDropdown,
  TimePicker,
  TimeEditor,
  Message,
  FilterList
};

const NonEditableSurvey = props => {
  const { survey, isMobile, closeAppointment } = props;
  const stepsArray = survey.steps;
  const [currentStep, setCurrentStep] = useState(-1);
  const [answers, setAnswers] = useState({});
  const [medicationList, setMedicationList] = useState(medications);
  const [isOptionalAnswered, setIsOptionalAnswered] = useState(false);
  const [selectedMedications, setSelectedMedications] = useState([]);
  const [allQuestions, setAllQuestions] = useState([]);

  /**
   *
   * @param {*} questionId
   * @param {*} answer List  of family member names if selected
   * @param {*} answered
   * @param {*} routeMap
   * @param {*} key
   */

  const steps = [
    { completed: true, questions: allQuestions, stepId: survey.steps[0].stepId }
  ];

  const storeAnswer = (
    questionId,
    answer,
    answered,
    routeMap,
    key,
    isOptAnswered = false
  ) => {
    answer.answered = answered;
    //Below is for Family history
    if (
      questionId.indexOf('family') > -1 &&
      isNullOrUndefined(answer?.noneOfTheAbove)
    ) {
      if (answer.noneOfTheAbove) {
        // If the checkbox "none of the above" is true, perform checking all 'N/A' checkbox checked in illness questionnaire
        for (let key in answer) {
          if (isArray(answer[key])) {
            answer[key] = ['N/A'];
          }
        }
      } else if (
        isNullOrUndefined(answers[questionId]?.noneOfTheAbove) &&
        answers[questionId]?.noneOfTheAbove &&
        !answer.noneOfTheAbove
      ) {
        // If earlier the "none of the above" is true and later changed to false, then uncheck all 'N/A' checckoxes in illnes questinarre
        for (let key in answer) {
          if (isArray(answer[key])) {
            answer[key] = [];
          } else if (key === 'answered') {
            answer[key] = false;
          }
        }
      }
    }
    //Below is for checkbox list applicable for medical and allergies & medication surveys
    if (
      questionId.indexOf('medical') > -1 ||
      questionId.indexOf('family') > -1 ||
      questionId.indexOf('medication') > -1
    ) {
      let list = answer?.selected;
      if (Array.isArray(list) && list.length > 1) {
        if (list[0] === 'N/A') {
          list.splice(0, 1);
        } else {
          const isNAExist =
            list.filter(function (val) {
              return val === 'N/A';
            }).length > 0;
          if (isNAExist) {
            list.splice(0, list.length - 1);
          }
        }
      }
    }

    if (routeMap && key) {
      answer.answerKey = key;
    }
    setIsOptionalAnswered(isOptAnswered);
    const newAnswers = { ...answers, [questionId]: answer };
    setAnswers(newAnswers);
    //Below logic for allergies and medication as it has filter with checkbox, input field and check box with NA.
    //TODO: Need to refactor below code
    if (questionId.indexOf('medication_s1') > -1) {
      const medicationsS2Items = {
        medication_s1BxU1: { selected: [], answered: true },
        medication_s1LCr5: { value: '', answered: true },
        medication_s154OE: { selected: [], answered: true }
      };
      let result = { [questionId]: answer };
      Object.keys(medicationsS2Items).forEach(item => {
        if (
          Object.keys(answers).filter(keyName => {
            return keyName === item;
          }).length === 0
        ) {
          if (item !== questionId) {
            result[item] = medicationsS2Items[item];
          }
        } else if (item === questionId) {
          const tempAnswer = { ...answer, answered: true };
          result[item] = tempAnswer;
        }
      });
      const newAnswers1 = { ...answers, ...result };
      setAnswers(newAnswers1);
    }
  };

  const analyzeQuestionDisplay = (answer, routeMap) => {
    if (!routeMap || !answer) {
      return;
    }
    const value = answer[answer.answerKey];
    const hideQuestionIds = get(routeMap, `[${value}].hide`);
    const showQuestionIds = get(routeMap, `[${value}].show`);
    map(hideQuestionIds, questionId => {
      const question = find(steps[currentStep].questions, [
        'questionId',
        questionId
      ]);
      question.display = false;
    });
    map(showQuestionIds, questionId => {
      const question = find(steps[currentStep].questions, [
        'questionId',
        questionId
      ]);
      question.display = true;
    });
  };

  const renderSurvey = () => {
    let nodes = [];
    if (currentStep > -1) {
      const step = steps?.[currentStep] || {};
      if (step) {
        const { questions } = step;
        let hideQuestionIdsList = [];
        for (let i = 0; i < questions?.length; i++) {
          const question = questions[i];
          const routeMap = get(question, 'details.routeMap');
          const answer = answers[question.questionId];
          let skipQuestions = false;
          if (answer?.answerKey) {
            const value = answer[answer.answerKey];
            const hideIds = get(routeMap, `[${value}].hide`);
            const showIds = get(routeMap, `[${value}].show`);
            if (hideQuestionIdsList.length > 0 && showIds?.length > 0) {
              hideQuestionIdsList.forEach(item => {
                showIds.forEach(item1 => {
                  if (item === item1) {
                    skipQuestions = true;
                  }
                });
              });
            } else {
              if (hideIds?.length > 0) {
                hideQuestionIdsList.push(...hideIds);
              }
            }
          }
          if (!skipQuestions) {
            analyzeQuestionDisplay(
              answers[question.questionId],
              get(question, 'details.routeMap')
            );
          }
          if (has(question, 'display') && !question.display) {
            continue;
          }
          const details = question.details;
          const Component = components[question.type];

          const rawAnswers =
            answers[question.questionId] ||
            get(survey, `answers.raw[0].${question.questionId}`, null);
          const customClassOther = question?.customClassOther;
          nodes.push(
            <Component
              {...details}
              index={`${currentStep}_${i}`}
              key={`${step.stepId}_${i}`}
              stepId={step.stepId}
              questionId={question.questionId}
              className={`question-block`}
              rawAnswers={rawAnswers}
              isMobile={isMobile}
              notRequired={details.notRequired}
              // checkSurveyState={this.checkSurveyState}
              checkSurveyState={() => { }}
              storeAnswer={storeAnswer}
              medicationList={medicationList}
              selectedList={rawAnswers || selectedMedications}
              setSelectedMedications={setSelectedMedications}
              customClassOther={customClassOther}
            />
          );
        }
      }
    }

    return nodes;
  };

  useEffect(() => {
    // recording all the answers if any in the begining
    const existingAnswers = get(survey, 'answers.raw[0]', {});
    setAnswers({ ...existingAnswers });

    // setting the current step
    let stepNo = -1;
    let allQuestions = [];
    forEach(steps, (step, index) => {
      if (!step.completed) {
        stepNo = Number(index);
        return false;
      }
    });
    if (stepNo === -1) {
      stepNo = 0;
    }
    map(stepsArray, step => {
      allQuestions = [...allQuestions, ...step.questions];
    });
    setAllQuestions(allQuestions);
    setCurrentStep(stepNo);
  }, [survey]);

  return (
    <>
      <Element backgroundColor="#f6f6f6" height="2px" margin="16px 0" />
      <Element pointerEvents="none">
        <form style={{ width: survey.width }}>
          {/* Rendering questions */}
          {renderSurvey()}
        </form>
      </Element>
      <Flex align="center" marginBottom="100px">
        <Button scheme="light" onClick={closeAppointment}>
          Close
        </Button>
      </Flex>
    </>
  );
};

export default NonEditableSurvey;
